import { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

/**
 * Configure the PDF.js worker. Using Vite's `new URL(..., import.meta.url)`
 * pattern so the worker ships with our bundle — no CDN, no extra
 * AppDomains entry, and works offline once the add-in is loaded.
 */
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface Props {
  /** Presigned or public HTTPS URL to the PDF. */
  url: string;
  /** 1-based page to scroll into view on load. */
  initialPage?: number;
  onError?: (err: Error) => void;
}

/**
 * Scroll-all-pages PDF viewer that works inside Word's WebView (WKWebView /
 * WebView2), which doesn't render PDFs natively via <iframe>.
 *
 * Design notes:
 *   - One <Page> per numbered page, sized to fit the container width.
 *   - No text or annotation layers — citations only need visual preview, and
 *     omitting them halves render time on big PDFs.
 *   - The `file` prop is memoized so react-pdf doesn't restart the fetch on
 *     every parent re-render.
 */
export function PdfViewer({ url, initialPage = 1, onError }: Props): JSX.Element {
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Jump to the citation's target page once the doc has loaded.
  useEffect(() => {
    if (!numPages || !containerRef.current || initialPage <= 1) return;
    const clamped = Math.min(initialPage, numPages);
    const target = containerRef.current.querySelector<HTMLElement>(
      `[data-page-index="${clamped}"]`,
    );
    if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [numPages, initialPage]);

  const handleLoadSuccess = useCallback((doc: { numPages: number }) => {
    setNumPages(doc.numPages);
  }, []);

  const handleLoadError = useCallback(
    (err: Error) => {
      setLoadError(err);
      onError?.(err);
    },
    [onError],
  );

  if (loadError) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <div className="text-xs text-amber-700">
          Could not render PDF inline ({loadError.message || 'network or CORS error'}).
          <br />
          Try the pop-out or "Open in browser" actions below.
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 overflow-y-auto bg-muted/40 scrollbar-thin"
    >
      <Document
        file={url}
        onLoadSuccess={handleLoadSuccess}
        onLoadError={handleLoadError}
        loading={
          <div className="py-10 text-center text-xs text-muted-foreground">Loading PDF…</div>
        }
        error={
          <div className="py-10 text-center text-xs text-amber-700">
            Failed to load PDF.
          </div>
        }
        className="flex flex-col items-center py-2"
      >
        {Array.from({ length: numPages }, (_, i) => (
          <div
            key={i}
            data-page-index={i + 1}
            className="my-2 bg-white shadow-sm"
          >
            <Page
              pageNumber={i + 1}
              width={width > 40 ? width - 24 : undefined}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </div>
        ))}
      </Document>
    </div>
  );
}
